import { AdBiddingProvider, IAdBiddingProviderConfig, IAdServiceAdapter } from '../..'
import { IConfiguration } from '../../../../../domain/conf'
import { Errors, Num } from '../../../../../utils/common'
import { logger } from '../../../../../utils/common/logger'
import { DOMUtils } from '../../../../../utils/dom'
import { Store } from '../../../../../utils/storage/keys'
import { IAd } from '../../../ad'
import { AdRefreshDOM } from '../../../ad'
import { IAdLazyLoadConf, IAdRefreshConf } from '../../../ad'
import { AdTargetKeys } from '../../../targeting/default'
import { AdTarget } from '../../../targeting/target'
import AAX from '../../vendors/ad-bidding/aax'
import Amazon from '../../vendors/ad-bidding/amazon'
import DistrictM from '../../vendors/ad-bidding/district-m'
import IndexExchange from '../../vendors/ad-bidding/index-exchange'
import Magnite from '../../vendors/ad-bidding/magnite'
import ParallelBiddingManager from '../../vendors/ad-bidding/parallel'
import IntegralAdScience from '../../vendors/ad-verifier/integral-ad-science'

export const REFRESH_KEY = 'refresh'
export const REFRESH_VALUE = 'true'

interface IWindow {
    googletag: any
    aax: any
    iasScores: string
    addEventListener: (eventName: string, listener: () => void) => void

    dispatchEvent(event: Event): void
}

declare let window: IWindow

export class GPTAdapter implements IAdServiceAdapter {
    /**
     * Singleton instance so we only initialize once
     */

    private static instance: GPTAdapter
    /**
     * initialRefreshAllDone : during initialization
     * we should only call refresh() (all) once to
     * avoid crashing the rendering rate
     * even when NewAdsEvent triggers requestAds
     */
    public initialRefreshAllDone = false
    public ads: IAd[] = []
    public adURI: string
    public lazyLoadConf: IAdLazyLoadConf
    public magnite: Magnite

    /**
     * The Google Publisher Tag library is now hosted at
     */
    private jsSrc = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js'

    /**
     * Default ad refresh configuration
     * See getAdConfFromOrDefault for further details
     */
    private defaultAdRefreshConf: IAdRefreshConf = {
        max: 5,
        s: 30,
    }

    /**
     * Default lazy load configuration
     * See getLazyLoadConfFromOrDefault for further details
     */
    private defaultLazyLoadConf: IAdLazyLoadConf = {
        fetchMarginPercent: 50, // 0.5 viewports
        mobileScaling: 2.0, // double values for mobile
        renderMarginPercent: 25, // 0.25 viewports
    }

    /**
     * Page data ad conf or defaultAdRefreshConf
     */
    private refreshConf: IAdRefreshConf

    private conf: IConfiguration

    /**
     * Bidding platforms
     */
    private aax: AAX
    private amazon: Amazon
    private districtm: DistrictM
    private ix: IndexExchange
    private parallel: ParallelBiddingManager

    private requestBind: () => void

    /**
     * Verifier platforms
     */
    private integralAdScience: IntegralAdScience

    /**
     * Temporary ads storage for emptying this.ads for Viafoura loading
     */
    private adsTemporaryStorage: IAd[]

    /**
     * Initializes GPT (instantiate global object and queue).
     */
    constructor(conf: IConfiguration, loadAdSlots: boolean) {
        this.conf = conf

        this.refreshConf = this.getRefreshAdConfFromOrDefault(this.conf)
        this.lazyLoadConf = this.getLazyLoadAdConfFromOrDefault(this.conf)

        // load GPT JS
        DOMUtils.loadScript(this.jsSrc, undefined, GPTAdapter.onGPTError)

        // setup queue
        window.googletag = window.googletag || {}
        window.googletag.cmd = window.googletag.cmd || []

        // setup queue for aax
        window.aax = window.aax || {}
        window.aax.cmd = window.aax.cmd || []

        window.aax.cmd.push(() => {
            if (window.aax.getAbpStatus && window.aax.getAbpStatus()) {
                window.googletag.cmd.push(() => {
                    window.googletag.pubads().refresh()
                })
            }
        })

        if (loadAdSlots) {
            // enqueue base setup
            window.googletag.cmd.push(() => {
                // This listener will be called when an impression becomes viewable.
                window.googletag.pubads().addEventListener('impressionViewable', this.impressionViewable.bind(this))

                // This listener will be called when a creative iframe load event fires.
                window.googletag.pubads().addEventListener('slotOnload', this.slotOnLoad.bind(this))

                // This listener will be called when a slot has finished rendering.
                window.googletag.pubads().addEventListener('slotRenderEnded', this.slotRenderEnded.bind(this))

                // This listener will be called when the specified service issues an ad
                // request for a slot. Each slot will fire this event, even though they may
                // be batched together in a single request if single request architecture
                // (SRA) is enabled.
                window.googletag.pubads().addEventListener('slotRequested', this.slotRequested.bind(this))

                // This listener will be called when an ad response has been received for
                // a slot.
                window.googletag.pubads().addEventListener('slotResponseReceived', this.slotResponseReceived.bind(this))

                // This listener will be called whenever the on-screen percentage of an ad
                // slot's area changes.
                window.googletag
                    .pubads()
                    .addEventListener('slotVisibilityChanged', this.slotVisibilityChanged.bind(this))

                // Enables/disables centering of ads. This mode must be set before the service is enabled.
                // Centering is disabled by default. In legacy gpt_mobile.js, centering is enabled by default.
                window.googletag.pubads().setCentering(true)

                // With lazy loading in SRA, when the first ad slot comes within
                // the viewport specified by the fetchMarginPercent parameter,
                // the call for that ad and all other ad slots is made.
                window.googletag.pubads().enableLazyLoad(this.lazyLoadConf)

                // Enables single request mode for fetching multiple ads at the same time.
                // This requires all pubads slots to be defined and added to the pubads
                // service prior to enabling the service.
                // Single request mode must be set before the service is enabled.
                window.googletag.pubads().enableSingleRequest()

                // By default, the behavior of the display() method is to
                // register, request, and render ad content into an ad slot.
                // The automatic requesting and rendering of ad content
                // can be disabled via : disableInitialLoad();
                window.googletag.pubads().disableInitialLoad()

                // Enables all GPT services that have been defined for ad slots on the page.
                window.googletag.enableServices()
            })
        }
    }

    /**
     * Get Singleton instance
     */
    public static getInstance(conf?: IConfiguration, loadSlots = true) {
        if (!GPTAdapter.instance) {
            GPTAdapter.instance = new GPTAdapter(conf, loadSlots)
        }
        return GPTAdapter.instance
    }

    /**
     * Delete Singletong instance
     * Needed for tests
     */
    public static deleteInstance() {
        GPTAdapter.instance = undefined
    }

    /**
     * Handler for gpt.js loading error, most likely due to ABP
     * @private
     */
    private static onGPTError() {
        logger('error', 'GPT loading error')
        window.googletag.error = true
        document.dispatchEvent(new Event(Errors.GPT_ERROR))
    }

    public destroy(ads = this.ads) {
        window.googletag.cmd.push(() => {
            window.googletag.destroySlots(ads.filter((_) => _.slot).map((_) => _.slot))
        })
        ads.forEach((ad) => {
            ad.destroy()
            this.ads = this.ads.filter((_) => _.id !== ad.id)
        })
    }

    /**
     * Calls `defineSlot` for given ad.
     *
     * @param ad
     */
    public async registerAd(ad: IAd): Promise<void> {
        const id = ad.id

        // store registered ads
        // tslint:disable-next-line:no-shadowed-variable
        if (this.ads.filter((ad) => ad.id === id).length === 0) {
            this.ads.push(ad)
        }
        return new Promise((resolve) => {
            // gpt.js error handling (most likely due to ABP)
            if (window.googletag.error) {
                resolve()
            }
            document.addEventListener(Errors.GPT_ERROR, () => {
                resolve()
            })

            // queue slot definition
            window.googletag.cmd.push(() => {
                // already destroyed
                if (!this.ads.find((_) => _.id === id)) {
                    resolve()
                }

                if (ad.isOutOfPage()) {
                    logger(
                        'debug',
                        `${ad.id}`,
                        `window.googletag.defineOutOfPageSlot(this.adURI, ad.id) with ${this.adURI}, ${ad.id}`
                    )
                    ad.slot = window.googletag.defineOutOfPageSlot(this.adURI, ad.id)
                } else {
                    logger(
                        'debug',
                        `${ad.id} window.googletag.defineSlot(`,
                        `this.filterAdUriOnCustomParam(ad), ad.validSizes(), ad.id) `,
                        `with ${this.filterAdUriOnCustomParam(ad)}, ${ad.validSizes()}, ${ad.id}`
                    )
                    ad.slot = window.googletag.defineSlot(this.filterAdUriOnCustomParam(ad), ad.validSizes(), ad.id)
                }
                logger('debug', `ad.slot.addService(window.googletag.pubads())`)
                if (ad.slot) { // check if ad slot still exists, may have been destroyed after adlight sign in
                    ad.slot.addService(window.googletag.pubads())

                    // add ad-level targeting
                    if (ad.targets) {
                        [...ad.targets].forEach((target: AdTarget) => {
                            logger(
                                'debug',
                                `${ad.id} ad.slot.setTargeting(target.key, target.value) with`,
                                `${target.key}, ${target.value}`
                            )
                            ad.slot.setTargeting(target.key, target.value)
                        })
                    }
                    if (ad.el.getAttribute(AdRefreshDOM.attr.state.key) !== AdRefreshDOM.attr.state.value.off) {
                        logger(
                            'debug',
                            `${ad.id} ad.slot.setTargeting(REFRESH_KEY, REFRESH_VALUE) with`,
                            `${REFRESH_KEY}, ${REFRESH_VALUE}`
                        )
                        ad.slot.setTargeting(REFRESH_KEY, REFRESH_VALUE)
                        logger(
                            'debug',
                            `${ad.id} ad.slot.setTargeting(AdTargetKeys.REFRESH, String(0)) with`,
                            `${AdTargetKeys.REFRESH}, ${String(0)}`
                        )
                        ad.slot.setTargeting(AdTargetKeys.REFRESH, String(0))
                    }
                }
                // clear event listeners
                document.removeEventListener(Errors.GPT_ERROR, () => {
                    resolve()
                })
                resolve()
            })
        })

    }

    /**
     * Filter adURI on custom targeting params
     */
    public filterAdUriOnCustomParam(ad: any) {
        let adURI = this.adURI

        ad.targets.forEach((_: any) => {
            if (_.value[0] === 'viafoura') {
                // tslint:disable-next-line: no-magic-numbers
                adURI = this.adURI.replace(this.adURI.split('/')[2], 'arn-viafoura')
            }
        })
        return adURI
    }

    /**
     * Register vendors.
     */
    public registerVendors() {
        this.parallel = new ParallelBiddingManager()

        // INITIALIZE BIDDING PLATFORMS HERE
        this.conf.ads.bidding.forEach((bidder: IAdBiddingProviderConfig) => {
            switch (bidder.client) {
                case AdBiddingProvider.AAX:
                    this.aax = new AAX()
                    this.aax.initializeBidding(this.conf.ads.bidding)
                    break
                case AdBiddingProvider.AMAZON:
                    this.amazon = new Amazon()
                    this.amazon.initializeBidding(this.conf.ads.bidding)
                    break
                case AdBiddingProvider.DISTRICT_M:
                    this.districtm = new DistrictM()
                    this.districtm.initializeBidding(this.conf.ads.bidding)
                    break
                case AdBiddingProvider.INDEX_EXCHANGE:
                    this.ix = new IndexExchange()
                    this.ix.initializeBidding(this.conf.ads.bidding)
                    break
                case AdBiddingProvider.MAGNITE:
                    this.magnite = new Magnite()
                    this.magnite.initializeBidding(this.conf.ads.bidding)
                    break
                default:
                    logger('info', `Unknown bidder configured: ${bidder.client} - Ignoring.`)
            }
        })
    }

    /**
     * Register Bidders for parallel bidding
     */
    public async registerBidders(ads: IAd[]) {
        this.requestBind = this.requestAds.bind(this)
        document.addEventListener('all-bidding-complete', this.requestBind)

        logger('debug', 'registering bidders: ',
            this.conf.ads.bidding.map((bidder: IAdBiddingProviderConfig) => bidder.client))

        this.conf.ads.bidding.forEach((bidder: IAdBiddingProviderConfig) => {
            this.parallel.registerBidder(this[bidder.client])
        })

        logger('debug', 'registered bidders, sending requests')

        await this.parallel.sendBidderRequests(ads)
    }

    /**
     * Calls `requestAds` enable gpt services.
     */
    public async initRequestAds(specificAds?: IAd[]): Promise<void> {
        const ads = specificAds || this.ads
        if (specificAds) { // store ads so this.ads can be emptied for Viafoura loading
            this.adsTemporaryStorage = this.ads.slice()
            this.ads = []
        }

        // ads destroyed since this was enqueued
        if (!ads.length) {
            logger('debug', 'exiting requestAds:GPT, ads array is empty: ', ads)
            return
        }

        logger('debug', 'registering ads', ads)
        for (const ad of ads) {
            await this.registerAd(ad)
        }
        logger('debug', 'ads registered, registering bidders')

        if (AAX.enabled) {
            await this.handleAAX(ads)
        } else {
            logger('debug', 'AAX disabled, registering bidders')
            await this.registerBidders(ads)
            logger('debug', 'bidders registered for the following ads: ', ads)
        }

        if (this.conf.ads.verifier) {
            this.integralAdScience = new IntegralAdScience()
            this.integralAdScience.initializeVerifier(this.conf.ads.verifier)
            this.integralAdScience.requestAds(ads, this.adURI)
            logger('log', `IAS initialized and requesting some ads`)
        }
    }

    /**
     * Calls `enableServices` to load ads, then `display` per ad.
     */
    public requestAds(evt?: Event, specificAds?: IAd[]) {
        document.removeEventListener('all-bidding-complete', this.requestBind)
        logger('info', `all-bidding-complete event - ${evt}}`)
        // ensure requestAds does not get called multiple times

        window.googletag.cmd.push(() => {
            const ads = specificAds || this.ads
            // ads destroyed since this was enqueued
            if (!ads.length) {
                return
            }

            logger('info', `isInitialLoadDisabled()=${window.googletag.pubads().isInitialLoadDisabled()}`)
            if (window.googletag.pubads().isInitialLoadDisabled()) {
                const publisherProvidedId = localStorage.getItem(Store.PPID)
                if (publisherProvidedId) {
                    logger('debug', `window.googletag.pubads().setPublisherProvidedId(${publisherProvidedId})`)
                    window.googletag.pubads().setPublisherProvidedId(publisherProvidedId)
                } else {
                    logger('debug', `GPT publisherProvidedId from localStorage was undefined,`,
                        `it won't be set as requested by Google ads`)
                }
                // With SRA activated, this call to display requests
                // all ad slots on the page
                // With initial load disabled, calls to display() will only register the ad slot
                // Ad will be fetched only when refresh is called.
                logger('info', `window.googletag.display(ads[0]), ads[0]=${ads[0].id}`)
                window.googletag.display(ads[0].id)
                if (!this.initialRefreshAllDone) {
                    logger('info', `window.googletag.pubads().refresh()`)
                    window.googletag.pubads().refresh()
                    this.initialRefreshAllDone = true

                    logger('info', 'All ads requested, sending out event for listeners')
                } else {
                    logger('info', `slot ${ads[0].id} window.googletag.pubads().refresh(ads[0]) with`, `${ads[0]}`)
                    window.googletag.pubads().refresh([ads[0].slot])
                }
            } else {
                ads.forEach((ad) => {
                    logger('info', `ad.id = ${ad.id}`)
                    logger('info', `window.googletag.display(ad.id), ad.id = ${ad.id}`)
                    window.googletag.display(ad.id)
                })
            }
        })
    }

    /**
     * Sets page-level targeting information.
     */
    public target(targets: AdTarget | AdTarget[]): void {
        window.googletag.cmd.push(() =>
            [].concat(targets).forEach((target) => window.googletag.pubads().setTargeting(target.key, target.value))
        )
    }

    /**
     * Remove/hide Ads if Empty response.
     * Check if event not empty before calling the function.
     *
     * @param e event object from google
     */
    public removeAdOnEmpty(e: any) {
        logger('debug', `slot ${e.slot.getSlotElementId()} removeAdOnEmpty event.isEmpty: ${e.isEmpty}`)
        const id = e.slot.getSlotId().getDomId()
        logger('debug', `slot ${e.slot.getSlotElementId()} removeAdOnEmpty this.ads.length: ${this.ads.length}`)
        const el = this.ads.find((ad) => ad.id === id)
        el.calculateRefreshSizes(document.querySelector(`#${id}`))
    }

    /**
     * Requests ias IAS scores
     * Upon response sends the result back to the calling function
     */
    public async getIasScores(): Promise<string> {

        return new Promise((resolve) => {
            const listener = this.listenToIasScore(resolve)
            if (window.iasScores) {
                resolve(window.iasScores)

            } else {

                window.addEventListener(
                    'ias_score_load_event',
                    listener
                )
            }
        })

    }

    public storeAd(ad: IAd): void {
        if (this.ads.filter((existingAd) => ad.id === existingAd.id).length === 0) {
            this.ads.push(ad)
        }
    }

    /**
     * Repopulate this.ads if previously stored for viafoura ad rendering
     */
    public repopulateAds(): void {
        if (this.adsTemporaryStorage && this.adsTemporaryStorage.length > 0) {
            this.ads.push(...this.adsTemporaryStorage)
        }
    }

    public listenToIasScore(resolve: (result: string) => void): () => void {
        return (): void => {
            resolve(window.iasScores)
        }
    }

    /**
     * Handles ad requests and bid loading for AAX
     * Takes in resolve() from the handleAAX function's Promise
     * @param ads - ads to request
     * @param resolve
     * @private
     */
    public async aaxRequestAds(ads: IAd[], resolve: (value?: (PromiseLike<unknown> | unknown)) => void) {
        if (window.aax.getAbpStatus && window.aax.getAbpStatus()) {
            this.requestAds({} as Event, ads)
            logger('debug', `requestAds:GPT is sent in
                        AAX call stack with Adblocker as true: ${ads}`)
            resolve()
        } else {
            logger('debug', 'AAX ABP disabled, registering bidders')
            await this.registerBidders(ads)
            logger('debug', `AAX call stack sent in
                    requestAds:GPT with Adblocker as false: ${ads}`)
            resolve()
        }
    }

    /**
     * Handles loading ads if AAX is enabled
     * @param ads - ads to load
     * @private
     */
    private async handleAAX(ads: IAd[]) {
        logger('debug', `AAX enabled`, window.aax)

        // if AAX loading has an error, do not attempt to load ads
        // likely the user's adblocker has blocked the loading of aax.js
        if (window.aax.error) {
            logger('debug', 'AAX loading error', window.aax.error)
            return
        }
        // await either an error or resolution of AAX
        await new Promise(async (resolve) => {
            document.addEventListener(Errors.AAX_ERROR, () => {
                logger('debug', 'AAX loading error event received')
                resolve()
            })
            // check if window.aax is loaded by reading "ver" property (returns version number as a string)
            if (window.aax.ver) {
                logger('debug', 'AAX loaded')
                await this.aaxRequestAds(ads, resolve)
            } else {
                // otherwise, if not loaded, push to cmd
                window.aax.cmd.push(async () => {
                    // no error, can check for ABP status from AAX
                    await this.aaxRequestAds(ads, resolve)
                })
            }
        })
    }

    /**
     * Returns the refresh count for a given slot, found in the targeting (rc).
     * 0 if not found
     * @param slot
     */
    private getCurrentRefreshCountFor(slot: any): number {
        return Array.isArray(slot.getTargeting(AdTargetKeys.REFRESH)) &&
        slot.getTargeting(AdTargetKeys.REFRESH).length === 0
            ? 0
            : parseInt(slot.getTargeting(AdTargetKeys.REFRESH)[0], 10)
    }

    /**
     * Slot has targeting option already set for refresh ("refresh" key)
     * @param slot
     */
    private isTargetedForRefresh(slot: any): boolean {
        logger(
            'debug',
            `slot ${slot.getSlotElementId()} carries the refresh targeting`,
            REFRESH_KEY,
            'with the value:',
            slot.getTargeting(REFRESH_KEY)
        )
        return slot.getTargeting(REFRESH_KEY).indexOf(REFRESH_VALUE) > -1
    }

    /**
     * Returns true if an ad slot has the value "off" for the attribute "data-ad-ref"
     * false otherwise
     * @param slot
     */
    private refreshDisabledByAttributeFor(slot: any): boolean {
        logger(
            'debug',
            `slot ${slot.getSlotElementId()} document.querySelector(#${slot.getSlotElementId()})=`,
            document.querySelector(`#${slot.getSlotElementId()}`)
        )
        if (null !== document.querySelector(`#${slot.getSlotElementId()}`)) {
            if (
                null !== document.querySelector(`#${slot.getSlotElementId()}`).getAttribute(AdRefreshDOM.attr.state.key)
            ) {
                logger(
                    'debug',
                    `slot ${slot.getSlotElementId()} has the attribute`,
                    AdRefreshDOM.attr.state.key,
                    'with the value:',
                    document.querySelector(`#${slot.getSlotElementId()}`).getAttribute(AdRefreshDOM.attr.state.key)
                )
                return (
                    AdRefreshDOM.attr.state.value.off ===
                    document.querySelector(`#${slot.getSlotElementId()}`).getAttribute(AdRefreshDOM.attr.state.key)
                )
            }
        }
        return false
    }

    /**
     * Async function to re-collect bids and refresh lot
     * @param slot - ad slot being refreshed
     * @param id - id of the element. Needed to pull the ad object from this.ads
     */
    private async refreshSlot(slot: any, id: string) {
        logger(
            'debug',
            `slot ${slot.getSlotElementId()}`,
            `window.googletag.pubads().refresh([slot]) with`,
            `${slot.getSlotElementId()}`,
            `Refreshing slot`
        )

        logger(
            `info`,
            `slot ${slot.getSlotElementId()}`,
            `adding new bids to slot before refresh`
        )

        const refreshedAd = this.ads.find((ad) => ad.id === id)
        await this.parallel.sendBidderRequests([refreshedAd], [slot])
        logger(
            `info`,
            `slot ${id}`,
            `bidder requests have returned now refresh ad`
        )
        window.googletag.pubads().refresh([slot])
    }

    /**
     * Create new Intersection Observer for ad slot to defer refresh until it is in viewport
     * @param slot - ad slot being refreshed
     * @param id - id of the element. Needed to pull the ad object from this.ads
     */
    private incrementRefreshCountAndRefreshSlot(slot: any, id: string) {
        const adDiv = document.getElementById(id) as HTMLElement
        // return if ad container was destroyed
        if (!adDiv) {
            return
        }
        this.incrementRefreshCount(slot)
        const observer = new IntersectionObserver(
            async (entries: IntersectionObserverEntry[], self: IntersectionObserver) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        await this.refreshSlot(slot, id)
                        self.unobserve(entry.target)
                    } else {
                        logger(
                            'debug',
                            `slot ${slot.getSlotElementId()}`,
                            'Setting Intersection Observer to monitor for ad slot in viewport',
                            'Future refresh will be queued'
                        )
                    }
                }
            }, {
                rootMargin: '50%', // trigger IO callback slightly before the container comes into viewport
                threshold: 0
            })

        observer.observe(adDiv)
    }

    /**
     * This event is fired when an impression becomes viewable, according to the Active View criteria.
     * https://support.google.com/admanager/answer/4524488
     * @param impressionViewableEvent
     */
    private impressionViewable(impressionViewableEvent: any): void {
        const slot = impressionViewableEvent.slot
        const id = slot.getSlotElementId()
        logger('debug', `slot ${slot.getSlotElementId()} became viewable.`)
        if (this.refreshConf !== undefined) {
            const isTargetedForRefresh = this.isTargetedForRefresh(slot)
            logger('debug', `slot ${slot.getSlotElementId()} isTargetedForRefresh:`, isTargetedForRefresh)
            const refreshDisabledByAttribute = this.refreshDisabledByAttributeFor(slot)
            logger('debug', `slot ${slot.getSlotElementId()} refreshDisabledByAttribute:`, refreshDisabledByAttribute)
            if (
                isTargetedForRefresh &&
                // slot can be dynamically altered by the creative (GAM config)
                // as of now mainly for interscrollers [6, 6] [7, 7]
                // in this case, the attribute "data-ad-ref" is set with the value "off"
                !refreshDisabledByAttribute
            ) {
                setTimeout(
                    this.incrementRefreshCountAndRefreshSlot.bind(this, slot, id),
                    this.refreshConf.s * Num.secToMs
                )
            } else {
                logger(
                    'debug',
                    `slot ${id}`,
                    'The slot was not targeted for refresh or was disabled'
                )
            }
        } else {
            logger('debug', `slot ${id}`, 'The page-data refresh configuration was undefined')
        }
    }

    /**
     * Increment the refresh count targeting value (rc) of a slot
     * @param slot
     */
    private incrementRefreshCount(slot: any) {
        const incrementedRefreshCount = this.getCurrentRefreshCountFor(slot) + 1
        slot.setTargeting(AdTargetKeys.REFRESH, String(incrementedRefreshCount))
        logger(
            'debug',
            `slot ${slot.getSlotElementId()}`,
            'slot.getTargeting(AdTargetKeys.REFRESH):',
            slot.getTargeting(AdTargetKeys.REFRESH)
        )
        logger('debug', `slot ${slot.getSlotElementId()}`, 'incremented rc value:', incrementedRefreshCount + 1)
    }

    /***
     * This event is fired when the creative's iframe fires its load event.
     * When rendering rich media ads in sync rendering mode,
     * no iframe is used so no SlotOnloadEvent will be fired.
     * @param slotOnloadEvent
     */
    private slotOnLoad(slotOnloadEvent: any): void {
        const slot = slotOnloadEvent.slot
        logger('debug', `slot ${slot.getSlotElementId()}`, `Creative iframe has loaded.`)
        const id = slot.getSlotId().getDomId()
        this.ads.find((ad) => ad.id === id).loaded()
    }

    /**
     * This event is fired when the creative code is injected into a slot.
     * This event will occur before the creative's resources are fetched,
     * so the creative may not be visible yet.
     * If you need to know when all creative resources for a slot have finished loading,
     * consider the SlotOnloadEvent instead.
     * @param slotRenderEndedEvent
     */
    private slotRenderEnded(slotRenderEndedEvent: any): void {
        // Log details of the rendered ad.
        logger('debug', 'Is empty?:', slotRenderEndedEvent.isEmpty)
        if (!slotRenderEndedEvent.isEmpty) {
            const slot = slotRenderEndedEvent.slot
            logger('debug', `slot ${slot.getSlotElementId()}`, 'finished rendering.')
            logger('debug', `slot ${slot.getSlotElementId()}`, 'Advertiser ID:', slotRenderEndedEvent.advertiserId)
            logger('debug', `slot ${slot.getSlotElementId()}`, 'Campaign ID: ', slotRenderEndedEvent.campaignId)
            logger('debug', `slot ${slot.getSlotElementId()}`, 'Creative ID: ', slotRenderEndedEvent.creativeId)
            logger('debug', `slot ${slot.getSlotElementId()}`, 'Line Item ID:', slotRenderEndedEvent.lineItemId)
            logger('debug', `slot ${slot.getSlotElementId()}`, 'Size:', slotRenderEndedEvent.size)
            logger(
                'debug',
                `slot ${slot.getSlotElementId()}`,
                'Source Agnostic Creative ID:',
                slotRenderEndedEvent.sourceAgnosticCreativeId
            )
            logger(
                'debug',
                `slot ${slot.getSlotElementId()}`,
                'Source Agnostic Line Item ID:',
                slotRenderEndedEvent.sourceAgnosticLineItemId
            )
            this.removeAdOnEmpty(slotRenderEndedEvent)
            const id = slotRenderEndedEvent.slot.getSlotId().getDomId()
            this.ads
                .find((ad) => ad.id === id)
                .display(slotRenderEndedEvent.size, slotRenderEndedEvent.slot.getResponseInformation().advertiserId)
        }
    }

    /**
     * This event is fired when an ad response has been received for a particular slot.
     * @param slotResponseReceivedEvent
     */
    private slotResponseReceived(slotResponseReceivedEvent: any): void {
        const slot = slotResponseReceivedEvent.slot
        logger('debug', `slot ${slot.getSlotElementId()}`, 'Ad response received.')
    }

    /**
     * This listener will be called when the specified service issues an ad
     * request for a slot. Each slot will fire this event, even though they may
     * be batched together in a single request if single request architecture
     * (SRA) is enabled.
     * @param slotRequestedEvent
     */
    private slotRequested(slotRequestedEvent: any): void {
        const slot = slotRequestedEvent.slot
        logger('debug', `slot ${slot.getSlotElementId()}`, 'has been requested.')
    }

    /**
     * This event is fired whenever the on-screen percentage of an ad slot's area changes.
     * The event is throttled and will not fire more often than once every 200ms.
     * @param slotVisibilityChangedEvent
     */
    private slotVisibilityChanged(slotVisibilityChangedEvent: any): void {
        const slot = slotVisibilityChangedEvent.slot
        logger('debug', `slot ${slot.getSlotElementId()} Visibility changed.`)

        // Log details of the event.
        logger(
            'debug',
            `slot ${slot.getSlotElementId()}`,
            'Visible area:',
            slotVisibilityChangedEvent.inViewPercentage + '%'
        )
    }

    /**
     * returns a IAdRefreshConf depending on page-data level refresh configuration or default
     * if refresh: undefined in page-data, will return empty object
     * if refresh: true in page-data, will return default configuration {max: 5, s: 30}
     * if refresh: false in page-data, will return undefined
     * if refresh: IAdRefreshConf, will retrurn that object
     * default: will return default configuration {max: 5, s: 30}
     * @param conf
     */
    private getRefreshAdConfFromOrDefault(conf: IConfiguration): IAdRefreshConf {
        switch (typeof conf.ads.refresh) {
            case 'undefined': {
                return undefined
            }
            case 'boolean': {
                return conf.ads.refresh ? this.defaultAdRefreshConf : undefined
            }
            case 'object': {
                return conf.ads.refresh
            }
        }
    }

    /**
     * returns a IAdLazyLoadConf depending on page-data level lazy configuration or default
     * if lazy: undefined in page-data, will return the default configuration
     * if lazy: false in page-data, will return the default configuration
     * if lazy: IAdRefreshConf, will retrurn that object
     * @param conf
     */
    private getLazyLoadAdConfFromOrDefault(conf: IConfiguration): IAdLazyLoadConf {
        switch (typeof conf.ads.lazy) {
            case 'undefined': {
                return this.defaultLazyLoadConf
            }
            case 'boolean': {
                return this.defaultLazyLoadConf
            }
            case 'object': {
                return conf.ads.lazy
            }
        }
    }
}
